import type * as T from "./types.js";

export class ApiError extends Error {
  constructor(public status: number, message: string, public data: Record<string, unknown> | null = null) {
    super(message);
  }
}

let onNeed2fa: () => void = () => undefined;
export function setNeed2faHandler(fn: () => void) { onNeed2fa = fn; }

let onUnauthorized: () => void = () => undefined;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

async function call<R>(method: string, url: string, body?: unknown): Promise<R> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: { "x-ffd": "1", ...(body !== undefined ? { "content-type": "application/json" } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, "Can't reach the dashboard server. Check your connection.");
  }
  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = null; }
  }
  if (!res.ok) {
    const msg = (data as { error?: string } | null)?.error ?? `Request failed (${res.status})`;
    if (res.status === 401 && !url.endsWith("/api/login")) onUnauthorized();
    if (res.status === 403 && (data as { need2fa?: boolean } | null)?.need2fa) onNeed2fa();
    throw new ApiError(res.status, msg, data as Record<string, unknown> | null);
  }
  return data as R;
}

export function qs(params: Record<string, string | number | (string | number)[] | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) { if (v.length) p.set(k, v.join(",")); }
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const api = {
  login: (username: string, password: string, code?: string, trust?: boolean) => call<{ ok?: true; needCode?: true; trustDays?: number }>("POST", "/api/login", { username, password, ...(code ? { code, trust: !!trust } : {}) }),
  twofaSetup: () => call<{ secret: string; uri: string }>("POST", "/api/me/2fa/setup", {}),
  twofaEnable: (code: string) => call<{ recoveryCodes: string[] }>("POST", "/api/me/2fa/enable", { code }),
  twofaDisable: (password: string) => call<{ ok: true }>("POST", "/api/me/2fa/disable", { password }),
  reset2fa: (id: number) => call<{ ok: true }>("POST", `/api/admin/users/${id}/2fa-reset`, {}),
  setRequire2fa: (on: boolean) => call<{ ok: true }>("POST", "/api/admin/settings", { require2fa: on }),
  logout: () => call<{ ok: true }>("POST", "/api/logout"),
  me: () => call<T.Me>("GET", "/api/me"),
  status: () => call<T.DataStatus>("GET", "/api/status"),
  scorecards: (q: string) => call<T.Scorecards>("GET", `/api/scorecards${q}`),
  trend: (q: string) => call<T.Trend>("GET", `/api/trend${q}`),
  map: (q: string) => call<T.CountyMap>("GET", `/api/map${q}`),
  opportunities: (q: string) => call<{ asOf: string; items: T.Opportunity[] }>("GET", `/api/opportunities${q}`),
  customer: (id: string) => call<T.CustomerDetail>("GET", `/api/customer/${encodeURIComponent(id)}`),
  changePassword: (current: string, next: string) => call<{ ok: true }>("POST", "/api/me/password", { current, next }),
  users: () => call<{ users: T.AdminUser[]; employees: T.Employee[]; require2fa: boolean }>("GET", "/api/admin/users"),
  createUser: (u: { username: string; name: string; role: "owner" | "manager"; employeeId: number | null; isAdmin: boolean; password: string }) => call<{ id: number }>("POST", "/api/admin/users", u),
  updateUser: (id: number, u: Partial<{ name: string; role: "owner" | "manager"; employeeId: number | null; isAdmin: boolean; disabled: boolean }>) => call<{ ok: true }>("POST", `/api/admin/users/${id}`, u),
  resetPassword: (id: number, password: string) => call<{ ok: true }>("POST", `/api/admin/users/${id}/password`, { password }),
  repTickets: (q: string) => call<{ line: string; items: T.RepTicket[] }>("GET", `/api/rep-tickets${q}`),
  conversion: (q: string) => call<T.Conversion>("GET", `/api/conversion${q}`),
  query: (spec: T.WidgetQuery) => call<T.QueryResult>("POST", "/api/query", spec),
  board: () => call<T.BoardWidget[]>("GET", "/api/board"),
  addWidget: (title: string, spec: T.WidgetSpec) => call<{ id: number }>("POST", "/api/board", { title, spec }),
  deleteWidget: (id: number) => call<{ ok: true }>("DELETE", `/api/board/${id}`),
  reorder: (ids: number[]) => call<{ ok: true }>("POST", "/api/board/order", { ids }),
  tokens: () => call<{ tokens: T.ApiToken[]; mcpUrl: string }>("GET", "/api/tokens"),
  createToken: (label: string) => call<{ token: string }>("POST", "/api/tokens", { label }),
  revokeToken: (id: number) => call<{ ok: true }>("DELETE", `/api/tokens/${id}`),
};
